import React, { useState } from "react";
import Modal from "react-bootstrap/Modal";
import { Formik } from "formik";
import * as Yup from "yup";
import { Button, Col, FloatingLabel, Form, Row } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useGetUsersCharacteristicListQuery } from "features/api/contentManagement/contentManagementApiSlice";
import { UsersCharacteristic } from "pages/ControlCenter/ContentManagement/contentManagementUtils/tabSchema";

import { useTranslation } from "react-i18next";
import { getAdjustedISOString } from "pages/ControlCenter/ContentManagement/contentManagementUtils/contentManagementUtils";
import { UserApp, ValidationUserApp, verifyStatuses } from "./volunteerTypes";
import { isValidIsraeliID } from "../../UsersPermissionsManagementUtils";

type Props = {
  isOpen: boolean;
  setIsOpen: (isOpen: null) => void;
  submitForm: (values: UserApp) => void;
  initialValues: UserApp;
};

const EditModal = ({ isOpen, setIsOpen, submitForm, initialValues }: Props) => {
  const { t } = useTranslation();
  const [selectedDateOfBirth, setSelectedDateOfBirth] = useState(
    new Date(initialValues?.dateOfBirth)
  );

  const { data: predefinedOptions }: { data: UsersCharacteristic[] } =
    useGetUsersCharacteristicListQuery();

  const validationSchema: Yup.ObjectSchema<ValidationUserApp> =
    Yup.object().shape({
      fullName: Yup.string()
        .max(20, t("חריגה מכמות התווים"))
        .required(t("required")),
      phoneNumber: Yup.string()
        .max(10, t("חריגה מכמות התווים"))
        .matches(/^0\d+$/, t("מספר הטלפון חייב להתחיל בספרה 0 ולהכיל רק ספרות"))
        .required(t("required")),
      email: Yup.string()
        .email(t("כתובת אימייל לא תקינה"))
        .matches(
          /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/g,
          t("כתובת אימייל לא תקינה")
        )
        .required(t("required")),
      tz: Yup.string()
        .max(9, t("חריגה מכמות התווים"))
        .test("isValidIsraeliID", t("תעודת זהות לא תקינה"), (value) =>
          isValidIsraeliID(value)
        )
        .required(t("required")),
    });

  const formikProps = {
    initialValues,
    onSubmit: submitForm,
    validationSchema,
  };

  return (
    <Modal
      size="lg"
      show={isOpen}
      onHide={() => setIsOpen(null)}
      aria-labelledby="contained-modal-title-vcenter"
      contentClassName="form-modal"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>{t("updateVolunteer")}</Modal.Title>
      </Modal.Header>
      <Formik {...formikProps}>
        {({
          handleSubmit,
          handleChange,
          touched,
          values,
          errors,
          setFieldValue,
        }) => {
          return (
            <>
              <Modal.Body>
                <Form>
                  <Row className="mb-4">
                    <Col className="px-3">
                      <Form.Group>
                        <FloatingLabel
                          controlId="floatingInput"
                          className="text-right"
                          label={t("volunteerId")}
                        >
                          <Form.Control
                            type="text"
                            name="id"
                            value={values.id}
                            disabled
                          />
                        </FloatingLabel>
                      </Form.Group>
                    </Col>
                    <Col className="px-3">
                      <Form.Group>
                        <FloatingLabel
                          controlId="floatingInput"
                          className="text-right"
                          label={t("volunteerName")}
                        >
                          <Form.Control
                            type="text"
                            name="fullName"
                            value={values.fullName}
                            onChange={handleChange}
                            maxLength={20}
                            isInvalid={touched?.fullName && !!errors?.fullName}
                          />
                          {errors?.fullName && (
                            <Form.Control.Feedback type="invalid">
                              {errors?.fullName}
                            </Form.Control.Feedback>
                          )}
                        </FloatingLabel>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="mb-4">
                    <Col className="px-3">
                      <Form.Group>
                        <FloatingLabel
                          controlId="floatingInput"
                          className="text-right"
                          label={t("phone")}
                        >
                          <Form.Control
                            type="string"
                            name="phoneNumber"
                            value={values.phoneNumber}
                            onChange={handleChange}
                            isInvalid={
                              touched?.phoneNumber && !!errors?.phoneNumber
                            }
                            maxLength={10}
                          />
                          {errors?.phoneNumber && (
                            <Form.Control.Feedback type="invalid">
                              {errors?.phoneNumber}
                            </Form.Control.Feedback>
                          )}
                        </FloatingLabel>
                      </Form.Group>
                    </Col>
                    <Col className="px-3">
                      <Form.Group>
                        <FloatingLabel
                          controlId="floatingInput"
                          className="text-right"
                          label={t("email")}
                        >
                          <Form.Control
                            type="email"
                            name="email"
                            value={values.email}
                            onChange={handleChange}
                            isInvalid={touched?.email && !!errors?.email}
                          />
                          {errors?.email && (
                            <Form.Control.Feedback type="invalid">
                              {errors?.email}
                            </Form.Control.Feedback>
                          )}
                        </FloatingLabel>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="mb-4">
                    <Col className="px-3">
                      <Form.Group>
                        <FloatingLabel
                          controlId="floatingInput"
                          className="text-right"
                          label={t("tz")}
                        >
                          <Form.Control
                            type="number"
                            name="tz"
                            value={values.tz}
                            onKeyDown={(e) =>
                              ["e", "E", "+", "-"].includes(e.key) &&
                              e.preventDefault()
                            }
                            onChange={(event) => {
                              const inputValue = event.target.value;
                              if (inputValue.length > 9) {
                                // Do not update the value if it exceeds the limit
                                return;
                              }
                              if (inputValue === "") {
                                // Handle erasing the value by setting it to an empty string
                                setFieldValue("tz", "");
                              } else {
                                // Convert the value to a number and update the field value
                                setFieldValue("tz", inputValue);
                              }
                            }}
                            isInvalid={touched?.tz && !!errors?.tz}
                            maxLength={9}
                            min={0}
                          />
                          {errors?.tz && (
                            <Form.Control.Feedback type="invalid">
                              {errors?.tz}
                            </Form.Control.Feedback>
                          )}
                        </FloatingLabel>
                      </Form.Group>
                    </Col>
                    <Col className="px-3">
                      <Form.Group>
                        <FloatingLabel
                          className="text-right"
                          label={t("startDate")}
                        >
                          <DatePicker
                            className="form-control"
                            id="floatingInput"
                            name="downloadTime"
                            selected={new Date(initialValues?.downloadTime)}
                            dateFormat="dd/MM/yyyy"
                            disabled
                          />
                        </FloatingLabel>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="mb-4">
                    <Col>
                      <Form.Group>
                        <FloatingLabel
                          controlId="floatingInput"
                          className="text-right"
                          label={t("status")}
                        >
                          <Form.Select
                            size="lg"
                            name="isVerifyByMimshalZamin"
                            defaultValue={values.isVerifyByMimshalZamin ? 1 : 0}
                            onChange={(event) =>
                              setFieldValue(
                                "isVerifyByMimshalZamin",
                                event.target.value !== "0"
                              )
                            }
                            className="square border border-secondary form-control select-icon-left"
                          >
                            {verifyStatuses.map(({ label, value }) => {
                              return (
                                <option key={value} value={value}>
                                  {label}
                                </option>
                              );
                            })}
                          </Form.Select>
                        </FloatingLabel>
                      </Form.Group>
                    </Col>
                    <Col>
                      <Form.Group>
                        <FloatingLabel
                          controlId="floatingInput"
                          className="text-right"
                          label={t("operationSystem")}
                        >
                          <Form.Control
                            type="text"
                            name="operationSystem"
                            value={[...new Set(values?.operationSystem)].join(
                              ", "
                            )}
                            disabled
                          />
                        </FloatingLabel>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="mb-4">
                    <Col>
                      <Form.Group>
                        <FloatingLabel
                          controlId="floatingInput"
                          className="text-right"
                          label={t("currentVersion")}
                        >
                          <Form.Control
                            type="text"
                            name="currentVersion"
                            value={values?.currentVersion?.join(", ")}
                            disabled
                          />
                        </FloatingLabel>
                      </Form.Group>
                    </Col>
                    <Col className="px-3">
                      <Form.Group>
                        <FloatingLabel
                          controlId="floatingInput"
                          className="text-right"
                          label={t("address")}
                        >
                          <Form.Control
                            type="text"
                            name="address"
                            value={values.address}
                            onChange={handleChange}
                          />
                        </FloatingLabel>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="mb-4">
                    <Col className="px-3">
                      <Form.Group>
                        <FloatingLabel
                          className="text-right"
                          label={t("dateOfBirth")}
                        >
                          <DatePicker
                            className="form-control"
                            id="floatingInput"
                            name="dateOfBirth"
                            selected={selectedDateOfBirth}
                            onKeyDown={(e) => e.preventDefault()}
                            onSelect={(date) => {
                              const adjustedISOString =
                                getAdjustedISOString(date);
                              setSelectedDateOfBirth(date);
                              setFieldValue("dateOfBirth", adjustedISOString);
                            }}
                            dateFormat="dd/MM/yyyy"
                            yearDropdownItemNumber={5}
                          />
                        </FloatingLabel>
                      </Form.Group>
                    </Col>
                    <Col className="px-3">
                      <Form.Group>
                        <FloatingLabel
                          className="text-right"
                          label={t("lastUpdate")}
                        >
                          <DatePicker
                            className="form-control"
                            id="floatingInput"
                            name="lastReportUpdate"
                            selected={new Date(initialValues?.lastReportUpdate)}
                            dateFormat="dd/MM/yyyy"
                            disabled
                          />
                        </FloatingLabel>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="mb-4">
                    <Col className="px-3">
                      <Form.Group>
                        <FloatingLabel
                          className="text-right"
                          label={t("usersCharacteristic")}
                        >
                          {predefinedOptions?.length > 0 ? (
                            <Form.Select
                              value={values.usersCharacteristic}
                              name="usersCharacteristic"
                              onChange={(event) => {
                                setFieldValue(
                                  "usersCharacteristic",
                                  event.target.value
                                );
                              }}
                              size="sm"
                            >
                              <option value="">
                                {t("selectCharacteristic")}
                              </option>
                              {predefinedOptions.map((option) => (
                                <option key={option.id} value={option.name}>
                                  {option.name}
                                </option>
                              ))}
                            </Form.Select>
                          ) : (
                            <Form.Control
                              as="input"
                              name="usersCharacteristic"
                              value={initialValues.usersCharacteristic}
                              onChange={handleChange}
                            />
                          )}
                        </FloatingLabel>
                      </Form.Group>
                    </Col>
                  </Row>
                </Form>
              </Modal.Body>
              <Modal.Footer className="flex-row">
                <Button variant="secondary" onClick={() => setIsOpen(null)}>
                  {t("cancel")}
                </Button>
                <Button variant="primary" onClick={() => handleSubmit()}>
                  {t("update")}
                </Button>
              </Modal.Footer>
            </>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default EditModal;
